import * as React from 'react';
import DesktopFilled from '@uber/base-icons/desktop-filled';
import Gear from '@uber/base-icons/gear';
import LightBulbFilled from '@uber/base-icons/light-bulb-filled';
import PhotosFilled from '@uber/base-icons/photos-filled';
import WeatherMoonFilled from '@uber/base-icons/weather-moon-filled';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { Avatar } from 'baseui/avatar';
import { StyledNavigationItem } from 'baseui/header-navigation';
import { NestedMenus, StatefulMenu } from 'baseui/menu';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { VerticalDivider } from '../../../../common/divider/divider';
import ToolbarButton from '../../../../common/toolbar-button';
export type ThemeOptions = 'dark' | 'light' | 'system';
type SettingOptions = 'theme';
const THEME_OPTIONS: ReadonlyArray<{
  id: ThemeOptions;
  text: string;
  Icon: typeof WeatherMoonFilled | typeof LightBulbFilled | typeof DesktopFilled;
  colorKey: keyof Theme['colors'];
}> = [{
  id: 'dark',
  text: 'Dark Theme',
  Icon: WeatherMoonFilled,
  colorKey: 'backgroundAccent'
}, {
  id: 'light',
  text: 'Light Theme',
  Icon: LightBulbFilled,
  colorKey: 'backgroundWarning'
}, {
  id: 'system',
  text: 'OS Default',
  Icon: DesktopFilled,
  colorKey: 'backgroundInverseSecondary'
}];
const SETTINGS_OPTIONS: ReadonlyArray<{
  id: SettingOptions;
  text: string;
  Icon: typeof PhotosFilled;
  colorKey: keyof Theme['colors'];
}> = [{
  id: 'theme',
  text: 'Theme',
  Icon: PhotosFilled,
  colorKey: 'contentPrimary'
}];
const SettingMenuItems = (theme: Theme) => SETTINGS_OPTIONS.map(opt => ({
  id: opt.id,
  label: <>
        <opt.Icon style={{
      marginRight: theme.sizing.scale400,
      color: theme.colors[opt.colorKey]
    }} />
        {opt.text}
      </>
}));
const ThemeMenuItems = (theme: Theme) => THEME_OPTIONS.map(opt => ({
  id: opt.id,
  label: <>
        <opt.Icon style={{
      marginRight: theme.sizing.scale400,
      color: theme.colors[opt.colorKey]
    }} />
        {opt.text}
      </>
}));
const MenuOverrides = ({
  close,
  theme,
  onThemeChange
}: {
  close: () => void;
  theme: Theme;
  onThemeChange: (theme: ThemeOptions) => void;
}) => ({
  Option: {
    props: {
      size: 'compact',
      getChildMenu: item => {
        if (item.id === 'theme') {
          return <StatefulMenu overrides={{
            List: {
              style: {
                width: theme.sizing.scale4800
              }
            },
            Option: {
              props: {
                size: 'compact'
              }
            }
          }} items={ThemeMenuItems(theme)} onItemSelect={({
            item
          }) => {
            onThemeChange?.((item.id as ThemeOptions));
            close();
          }} />;
        }
        return null;
      }
    }
  }
});
type PropsT = {
  userName: string;
  onThemeChange: (theme: ThemeOptions) => void;
};
export function LabelingSettings({
  userName,
  onThemeChange
}: PropsT) {
  const [, theme] = useStyletron();
  return <>
      <VerticalDivider />
      <StyledNavigationItem $style={{
      padding: 0
    }}>
        <Avatar name={userName} size="scale900" src="" />
      </StyledNavigationItem>
      <StyledNavigationItem $style={({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      paddingLeft: $theme.sizing.scale500
    })}>
        <StatefulPopover placement={PLACEMENT.bottomRight} autoFocus={false} focusLock={false} content={({
        close
      }) => <NestedMenus>
              <StatefulMenu items={SettingMenuItems(theme)} overrides={MenuOverrides({
          close,
          theme,
          onThemeChange
        })} />
            </NestedMenus>}>
          <ToolbarButton customStyles={{
          marginLeft: 0
        }} title="" onClick={() => {}} attributes={{
          'data-testid': 'settings-button',
          'data-selector-id': 'settings-button'
        }}>
            <Gear size={16} />
          </ToolbarButton>
        </StatefulPopover>
      </StyledNavigationItem>
    </>;
}